import {
  Modal,
  UseModalProps,
  ButtonRow,
  formatDate,
  dayjs,
  DATE_FORMAT_ISO_NO_TIME,
  Form,
  DateField,
  Dayjs,
  FormSubmit,
  TextButton,
  FlexLoadingBlock,
} from "@synota-io/synota-shared-ui";
import { Typography } from "@mui/material";
import { useInvoiceDetails } from "../../../../api/hooks/useInvoiceDetails";
import { InvoiceDetailsList } from "./InvoiceDetailsList";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { generatePath, useNavigate } from "react-router-dom";
import { CONTRACT_INVOICES_INVOICE_DETAILS_PATH } from "../../../../paths";
import { useState, ReactNode } from "react";
import { Edit } from "@mui/icons-material";

interface Props extends UseModalProps {
  contract_shared_uuid: string | undefined;
  energy_date: string | undefined;
}

const schema = yup.object({
  energy_date: yup.object().required(""),
});

type FormValues = {
  energy_date: Dayjs | null;
};

export const InvoiceDetailsModal = ({
  open,
  onClose,
  contract_shared_uuid,
  energy_date,
}: Props) => {
  const navigate = useNavigate();
  const { invoiceDetails, isLoading } = useInvoiceDetails(
    { contract_shared_uuid, energy_date: dayjs(energy_date).format(DATE_FORMAT_ISO_NO_TIME) },
    { enabled: open, staleTime: 0, gcTime: 0 },
  );
  const [showDateField, setShowDateField] = useState(false);

  const { control, handleSubmit, formState } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      energy_date: energy_date ? dayjs(energy_date) : null,
    },
  });

  const onSubmit = handleSubmit((values) => {
    if (!contract_shared_uuid || !values.energy_date) {
      return;
    }
    navigate(
      generatePath(CONTRACT_INVOICES_INVOICE_DETAILS_PATH, {
        id: contract_shared_uuid,
        date: values.energy_date.toISOString(),
      }),
      { replace: true },
    );
    setShowDateField(false);
  });

  if (!energy_date || !contract_shared_uuid) {
    return null;
  }

  const title: ReactNode = (
    <Typography variant="h6">
      Invoice Details |{" "}
      <TextButton onClick={() => setShowDateField(!showDateField)}>
        <Typography color="text.primary" component="span" variant="h6">
          {formatDate(energy_date)}
          <Edit sx={{ ml: 2, pt: 1 }} color="primary" fontSize="small" />
        </Typography>
      </TextButton>
    </Typography>
  );

  return (
    <Modal
      preventCloseOnBackdropClick
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      title={title}
    >
      {showDateField && (
        <Form id="invoice-date-form" onSubmit={onSubmit}>
          <ButtonRow pb={4} width="100%" alignItems="end">
            <DateField autoFocus fullWidth control={control} name="energy_date" clearable />
            <FormSubmit disabled={!formState.isValid} form="invoice-date-form" label="View" />
          </ButtonRow>
        </Form>
      )}
      {isLoading ? (
        <FlexLoadingBlock>Please wait...</FlexLoadingBlock>
      ) : invoiceDetails?.data ? (
        <InvoiceDetailsList data={invoiceDetails.data} />
      ) : (
        <Typography textAlign="center" py={5}>
          No invoice details available.
        </Typography>
      )}
    </Modal>
  );
};
